import React, {useCallback, useMemo, useState} from 'react'

import EditIcon from '@mui/icons-material/Edit'
import { IconButton } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid'

import {
  ALL_NUMBER_OPERATORS,
  createAutocompleteOperator,
  createNotEqualsAutocompleteOperator,
  SELECT_OPERATOR,
  NOT_EQUAL_SELECT_OPERATOR,
  CONTAINS_STRING_OPERATOR,
  NOT_CONTAINS_STRING_OPERATOR,
} from 'constants/table-filters'

import { useActions } from 'hooks/useActions'
import { usePagination } from 'hooks/usePagination'
import { useSelectedRows } from 'hooks/useSelectedRows'

import {
  useAssignCardMutation,
  useGetAccountsQuery,
  useGetCardTypesAndAccountNamesQuery,
} from 'services/purchasing/api'
import { BOT_STATUSES, BotStatus } from 'services/purchasing/types'

import { AssignCardModal } from 'components/common/AssignCardModal'
import CardInfoPopup from 'components/common/CardInfoPopup'
import { CopyToClipboard } from 'components/common/CopyToClipboard/index'
import { DetailsModal } from 'components/common/DetailsModal'
import InWorkTableToolbar from 'components/purchasing/in-work/InWorkTableToolbar'
import CustomDataGrid from 'components/table/DataGrid'

export type InWorkTabActions =
  | 'REGISTER_APP'
  | 'CONFIRM'
  | 'CHANGE_PASSWORD'
  | 'INFO'
  | 'ASSIGN_CARD'
  | 'CARD_INFO'
  | 'CONFIRM_SINGLE'
  | 'REGISTER_APP_SINGLE'
  | 'CHANGE_PASSWORD_SINGLE'
  | ''
const InWorkTab = () => {
    const [isHighlight, setIsHighlight] = useState<boolean>(false)
    const handleCellHighlight = (field: string) => {
        console.log(field)
        if (['password', 'login'].includes(field)) {
            setIsHighlight(true)
        }
    }
  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        type: 'Number',
        flex: 1,
        hideable: false,
        headerAlign: 'left',
        align: 'left',
        filterOperators: ALL_NUMBER_OPERATORS,
      },
      {
        field: 'archivedStatus',
        headerName: 'Status',
        headerAlign: 'center',
        align: 'center',
        flex: 2,
        type: 'singleSelect',
        valueOptions: BOT_STATUSES,
        valueFormatter: ({ value }) => BotStatus[value as keyof typeof BotStatus],
        // @ts-ignore
        filterOperators: [SELECT_OPERATOR, NOT_EQUAL_SELECT_OPERATOR],
      },
      {
        field: 'email',
        headerName: 'Bot User',
        type: 'String',
        flex: 4,
        valueGetter: (row) => row.row.login,
        filterOperators: [
          createAutocompleteOperator('/api/purchasing/bots/distinct', 'email'),
          createNotEqualsAutocompleteOperator('/api/purchasing/bots/distinct', 'email'),
          ...CONTAINS_STRING_OPERATOR,
          ...NOT_CONTAINS_STRING_OPERATOR,
        ],
        renderCell: ({ row }) => <CopyToClipboard value={row?.account?.email} field="login" handleCellHighlight={handleCellHighlight}/>,
      },
      {
        field: 'password',
        headerName: 'Bot password',
        sortable: false,
        type: 'string',
        flex: 2,
        filterable: false,
        renderCell: ({ row }) => <CopyToClipboard value={row?.password} field="password" handleCellHighlight={handleCellHighlight} />,
      },
      {
        field: 'ts_name',
        headerName: 'Bot Source',
        flex: 3,
        type: 'String',
        valueGetter: ({ row }) => row?.ticketSystem?.name,
        filterOperators: [
          createAutocompleteOperator('/api/purchasing/bots/distinct', 'ts_name'),
          createNotEqualsAutocompleteOperator(
            '/api/purchasing/bots/distinct',
            'ts_name'
          ),
          ...CONTAINS_STRING_OPERATOR,
          ...NOT_CONTAINS_STRING_OPERATOR,
        ],
      },
      {
        field: 'details',
        headerName: 'Details',
        flex: 2,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleDetailsClick(row)
            }}
          >
            Show Details
          </Button>
        ),
      },
      {
        field: 'card',
        headerName: 'Credit Cards',
        type: 'string',
        flex: 2,
        sortable: false,
        filterable: false,
        renderCell: ({ row, value }) => {
          return (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
                minWidth: '100%',
              }}
            >
              {value ? (
                <Button
                  size="small"
                  variant="text"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedRows([row.id])
                    setModal('CARD_INFO')
                  }}
                >
                  <Typography
                    sx={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                  >
                    {value.num}
                  </Typography>
                </Button>
              ) : (
                <Typography
                  fontSize="14px"
                  pl={1}
                  sx={{
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                >
                  Not assigned
                </Typography>
              )}
              <IconButton
                sx={{ alignSelf: 'center' }}
                aria-label="copy"
                color="primary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  setModal('ASSIGN_CARD')
                  setSelectedRows([row.id])
                }}
              >
                <EditIcon sx={{ cursor: 'pointer' }} />
              </IconButton>
            </Box>
          )
        },
      },
      {
        field: 'actions',
        type: 'actions',
        getActions: (params: GridRowParams) => {
          return [
            <GridActionsCellItem
              key={params.id}
              onClick={() => {
                setModal('CONFIRM_SINGLE')
                setSingleRow(params.row.id)
              }}
              label="Remove from work"
              showInMenu
            />,
            <GridActionsCellItem
              key={params.id}
              onClick={() => {
                setModal('REGISTER_APP_SINGLE')
                setSingleRow(params.row.id)
              }}
              label="Register Application"
              showInMenu
            />,
            <GridActionsCellItem
              key={params.id}
              onClick={() => {
                setModal('CHANGE_PASSWORD_SINGLE')
                setSingleRow(params.row.id)
              }}
              label="Change Password"
              showInMenu
            />,
          ]
        },
      },
    ],
    []
  )
  const [assignCard, { isLoading: isLoadingAssignCard }] = useAssignCardMutation()
  const { data: dataAssign, isFetching: isFetchingDataAssign } =
    useGetCardTypesAndAccountNamesQuery()
  const { modal, setModal, singleRow, setSingleRow } = useActions<InWorkTabActions>()
  const { queryParams, paginationDataGridProps } = usePagination({ columns })
  const { selectedRows, setSelectedRows, handleRowSelection } = useSelectedRows()
  const { data, isFetching } = useGetAccountsQuery({ ...queryParams, showTaken: true })
  const selectedAccount = data?.data.filter((row) => selectedRows.includes(row.id))?.[0] || null

  const handleDetailsClick = useCallback((row: any) => {
    setModal('INFO')
    setSelectedRows([row])
  }, [])
  return (
    <Box sx={{ height: 'calc(100vh - 122px)', width: '100%', pt: 1 }}>
      <CustomDataGrid<InWorkTabActions>
        rows={data?.data || []}
        columns={columns}
        rowCount={data?.page?.totalElements || 0}
        loading={isFetching}
        {...paginationDataGridProps}
        onRowSelectionModelChange={handleRowSelection}
        selectedRows={selectedRows}
        toolbar={InWorkTableToolbar}
        rowHeight={70}
        checkboxSelection
        modal={modal}
        setModal={setModal}
        singleRow={singleRow}
        onCellClick={() => setIsHighlight(false)}
        sx={{
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                outline: isHighlight ? '' : 'none',
            },
        }}
      />
      {modal === 'INFO' && (
        <DetailsModal
          onClose={() => {
            setModal('')
            setSelectedRows([])
          }}
          item={selectedRows[0]}
        />
      )}
      {modal === 'ASSIGN_CARD' && selectedAccount && (
        <AssignCardModal
          assignCardHandler={assignCard}
          isLoading={isLoadingAssignCard}
          data={dataAssign}
          isFetching={isFetchingDataAssign}
          onClose={() => setModal('')}
          selectedAccount={selectedAccount}
        />
      )}
      {modal === 'CARD_INFO' && selectedAccount && (
        <CardInfoPopup onClose={() => setModal('')} card={selectedAccount.card!} />
      )}
    </Box>
  )
}

export default InWorkTab
